import React, { FC, useMemo, useState } from 'react';
import classNames from 'classnames';

import Icon from './icon.svg';

import s from './SidePanel.scss';
import { useTranslation } from '@wix/wix-i18n-config';
import { Dismiss } from '@wix/wix-ui-icons-common';
import { Accordion } from '../Accordion/Accordion';
import { useInitialState } from '../../contexts/InitialStateContext';
import { useBiLogger } from '../../contexts/BILoggerContext';
import { sidePanelDataHooks as dataHooks } from './SidePanel.dataHooks';
import { useParamsWithDefaults } from '../../hooks/useParamsWithDefaults';
import { useFedopsLogger } from '../../contexts/FedopsLoggerContext';
import { useCategoryGroups } from '../../hooks/useCategoryGroups';
import { usePageScroll } from '../../contexts/PageScrollContext';
import { useInternalRoutes } from '../../hooks/useInternalRoutes';

export const SidePanel: FC = () => {
  const [mobileActive, setMobileActive] = useState(false);
  const { t } = useTranslation();
  const { book } = useInitialState();
  const { categorySlug } = useParamsWithDefaults();
  const { reportBrowseSidebar } = useBiLogger();
  const fedopsLogger = useFedopsLogger();
  const categoryGroups = useCategoryGroups();
  const { scrollToTop } = usePageScroll();
  const { templates } = useInternalRoutes();

  const accordionData = useMemo(
    () =>
      categoryGroups.map(({ title, categorySlugs }) => ({
        title: t(title),
        content: categorySlugs.map((slug) => ({
          url: templates.build(slug),
          active: slug === categorySlug,
          label:
            book?.categories.find((c) => c.slug === slug)?.displayName ?? slug,
        })),
      })),
    [categoryGroups, t, categorySlug, book?.categories],
  );

  const browseBy = (
    <span className={s.SidePanel__browseByLabel}>
      <Icon />
      {t('app.new.sidepanel.button')}
    </span>
  );

  return (
    <>
      <div
        className={s.SidePanel__mobile}
        onClick={() => setMobileActive(true)}
      >
        {browseBy}
      </div>
      <div
        data-hook={dataHooks.container()}
        className={classNames(s.SidePanel, {
          [s.SidePanel__mobileActive]: mobileActive,
        })}
      >
        {mobileActive && (
          <div
            onClick={() => setMobileActive(false)}
            className={s.SidePanel__mobileBackdrop}
          />
        )}
        <div className={s.SidePanel__content}>
          {mobileActive && (
            <button
              className={s.SidePanel__dismiss}
              onClick={() => setMobileActive(false)}
            >
              <Dismiss color="black" />
            </button>
          )}
          <div style={{ marginBottom: 14 }}>{browseBy}</div>

          <Accordion
            data={accordionData}
            onItemClick={(bucketName: string) =>
              reportBrowseSidebar(bucketName)
            }
            onSubitemClick={(categoryName: string) => {
              reportBrowseSidebar(categoryName);
              fedopsLogger.startCategorySwitchInteraction();
              scrollToTop();
              setMobileActive(false);
            }}
          />
        </div>
      </div>
    </>
  );
};
